/**
 * [AI補完仕様] このコメントはAI補完（コード自動生成）に構造情報として渡されます。変更時は実装品質に直結するため正確に保つこと。
 *
 * 認証トークンの外部ストア — Reactコンポーネント外（axiosインターセプター等）からも同期的に参照・更新できる単一の情報源。
 * Access Tokenはメモリ上にのみ保持する（sessionStorage/localStorageに保存しない。XSS時の露出を最小化するため）。
 * Refresh Tokenはこのモジュールでは一切扱わない。httpOnly Cookieでブラウザが自動送受信するため、JS側は関与しない。
 *
 * @contract Access Tokenの参照・更新は必ずこの関数群を使うこと。sessionStorage等に独自保存しない。
 * @contract Reactコンポーネントからトークンを購読する場合はsubscribeTokens()をuseSyncExternalStoreに渡すこと。
 * @contract Access Tokenのリフレッシュが必要な箇所（401応答時等）は必ずrefreshAccessToken()を使うこと。個別に/api/auth/refreshを呼ばない（同時多発時の重複リフレッシュを防止するため）。
 * @contract Cookie経由のリクエスト（/api/auth/refresh, /api/auth/logout等）はwithCredentials: trueとgetCsrfToken()によるX-CSRF-Tokenヘッダーを付与すること。
 * @signature getAccessToken(): string | null
 * @signature setAccessToken(accessToken: string | null): void
 * @signature clearTokens(): void
 * @signature subscribeTokens(listener: () => void): () => void
 * @signature refreshAccessToken(): Promise<string> — 失敗時はトークンをクリアしてthrowする
 * @signature getCsrfToken(): string | null — CSRF Cookie（JSから読める非httpOnly Cookie）の値を取得する
 */
import axios from 'axios';

const API_BASE = '/api';
const CSRF_COOKIE_NAME = 'csrf_token';

type Listener = () => void;

const listeners = new Set<Listener>();

// 旧実装（CSRF Cookieをpath=/api/authで発行していた版）が残した古いCookieをクリアする。
// path違いの同名Cookieがブラウザに残ると、/api/auth配下へのリクエストには新旧両方が送信される一方、
// document.cookie（ページのpath=/から見た場合）には新しい方（path=/）しか見えず、
// サーバー側の検証値とクライアント側の送信値が食い違ってCSRF検証が常に失敗し続ける不具合を防ぐ。
// document.cookieへの代入は、対象Cookieが現在のpathから不可視でも、同じpathを指定すれば削除できる。
if (typeof document !== 'undefined') {
  document.cookie = `${CSRF_COOKIE_NAME}=; Path=/api/auth; Max-Age=0`;
}

// メモリ上にのみ保持。ページリロードで消える（起動時にrefreshAccessToken()でCookieから再取得する設計）
let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(newAccessToken: string | null): void {
  accessToken = newAccessToken;
  listeners.forEach(listener => listener());
}

export function clearTokens(): void {
  setAccessToken(null);
}

export function subscribeTokens(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// 同時に複数箇所（axiosインターセプター・起動時のセッション復元等）で401が発生しても、
// リフレッシュは1回にまとめる（進行中のPromiseを共有する）。
let refreshPromise: Promise<string> | null = null;

export async function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const csrfToken = getCsrfToken();
        const res = await axios.post(`${API_BASE}/auth/refresh`, {}, {
          withCredentials: true,
          headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
        });
        const data = res.data.data ?? res.data;
        setAccessToken(data.token);
        return data.token as string;
      } catch (err) {
        clearTokens();
        throw err;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}
